/**
 * Client Care cadence UI helpers (pure — no React / no Firebase) so the
 * month-cadence normalization is unit-testable in isolation from the page.
 */
import type { ClientCareConfig, CadenceRule } from '../../hooks/useClientCareConfig';

// Cadences whose timing is expressed in MONTHS and support admin-managed
// per-broker overrides: the one-time settlement check-in (offset AFTER settlement)
// and the recurring periodic review (frequency). Everything else keeps its
// existing day-based inputs.
export const MONTH_CADENCES = new Set(['post_settlement', 'periodic_review']);
export const MONTH_CADENCE_DEFAULT = 6; // company default when none set (6 months)

/**
 * A cadence duration as the UI edits it: exactly ONE unit is ever set.
 *
 * Weeks exists because a 6-week post-settlement check-in is not expressible in
 * months, and the business intent ("one repayment has cleared") is lost when it
 * is rounded to 1 or 2. Backend precedence is weeks > months > legacy days.
 */
export interface CadenceDuration {
  weeks?: number;
  months?: number;
}

/** The duration currently on a rule/override, defaulting to the company month default. */
export function durationOf(
  rule: { intervalWeeks?: number; intervalMonths?: number } | undefined,
  fallbackMonths: number = MONTH_CADENCE_DEFAULT,
): CadenceDuration {
  if (rule?.intervalWeeks != null) return { weeks: rule.intervalWeeks };
  if (rule?.intervalMonths != null) return { months: rule.intervalMonths };
  return { months: fallbackMonths };
}

/** True when the duration carries no unit at all (per-broker "company default"). */
export function isInherited(rule: { intervalWeeks?: number; intervalMonths?: number } | undefined): boolean {
  return rule?.intervalWeeks == null && rule?.intervalMonths == null;
}

/** Human label for a duration, e.g. "6 weeks" / "3 months". */
export function formatDuration(d: CadenceDuration): string {
  if (d.weeks != null) return `${d.weeks} ${d.weeks === 1 ? 'week' : 'weeks'}`;
  const m = d.months ?? MONTH_CADENCE_DEFAULT;
  return `${m} ${m === 1 ? 'month' : 'months'}`;
}

/** Short label for the per-broker "Applies" column, e.g. "every 6 wk" / "3 mo after". */
export function formatApplies(d: CadenceDuration, recurring: boolean): string {
  const short = d.weeks != null ? `${d.weeks} wk` : `${d.months ?? MONTH_CADENCE_DEFAULT} mo`;
  return recurring ? `every ${short}` : `${short} after`;
}

/**
 * Ensure every ENABLED month-based cadence carries an explicit `intervalMonths`
 * (migrated from a legacy `intervalDays`, else the default) and drop the stale
 * `intervalDays`. The MonthsField shows `intervalMonths ?? 6` as a display-only
 * fallback, but that value must actually be persisted — otherwise a fresh
 * toggle-on saves a rule with NO interval: `periodic_review` then generates zero
 * events and `post_settlement` falls to its 30-day default while the UI still
 * reads "6 months". Applied on load (honest display of existing configs) and at
 * save (guarantees the persisted value matches the UI regardless of interaction).
 * Idempotent.
 */
export function normalizeMonthCadences(cfg: ClientCareConfig): ClientCareConfig {
  const cadences: Record<string, CadenceRule> = { ...cfg.cadences };
  let changed = false;
  for (const key of MONTH_CADENCES) {
    const rule = cadences[key];
    if (!rule || !rule.enabled) continue;
    // Week-based rules are already explicit. Weeks is authoritative, so strip
    // any other unit rather than converting it: keeping both would leave the
    // backend's weeks > months precedence as invisible state on the document.
    if (rule.intervalWeeks != null) {
      if (rule.intervalDays != null || rule.intervalMonths != null) {
        const { intervalDays: _dropD, intervalMonths: _dropM, ...rest } = rule;
        cadences[key] = { ...rest, intervalWeeks: rule.intervalWeeks };
        changed = true;
      }
      continue;
    }
    if (rule.intervalMonths == null || rule.intervalDays != null) {
      const months =
        rule.intervalMonths ??
        (rule.intervalDays != null ? Math.max(1, Math.round(rule.intervalDays / 30)) : MONTH_CADENCE_DEFAULT);
      const { intervalDays: _dropDays, ...rest } = rule;
      cadences[key] = { ...rest, intervalMonths: months };
      changed = true;
    }
  }
  return changed ? { ...cfg, cadences } : cfg;
}
